import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
  timelineOppositeContentClasses
} from "@mui/lab";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme
} from "@mui/material";
import {
  ChevronRight20Px,
  ConfirmationModal,
  Edit20Px,
  ErrorPanel,
  PlusCircle20Px,
  Remove20Px,
  useToast
} from "@qubit/autoparts";
import dayjs from "dayjs";
import type { Duration } from "dayjs/plugin/duration";
import { useState } from "react";
import { useTranslation } from "react-i18next";

import {
  scheduleDayAccordionSx,
  scheduleDayRowsSx,
  scheduleEntryCardSx
} from "~/features/preview/batches/batchSettings.styles";
import CreateRecurringScheduleModal from "~/features/settings/batchSettings/CreateRecurringScheduleModal";
import EditRecurringScheduleModal from "~/features/settings/batchSettings/EditRecurringScheduleModal";
import { parseTimeStringToDuration } from "~/lib/dayjs";
import {
  dayOfWeekAsString,
  daysOfTheWeek,
  DayOfWeekMap,
  DayOfWeekValue
} from "~/lib/daysOfTheWeek";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { groupBy } from "~/lib/shared";
import {
  useDeleteRecurringScheduleMutation,
  useGetRecurringSchedulesQuery
} from "~/redux/warehouse/recurringSchedule.hooks";
import { useGetStagingAreasQuery } from "~/redux/warehouse/stagingArea.hooks";
import { RecurringScheduleDto } from "~/types/api";

import { ScheduleOrderCount } from "./ScheduleOrderCount";

function startOfWindowDuration(
  recurringSchedule: RecurringScheduleDto
): Duration {
  return parseTimeStringToDuration(recurringSchedule.scheduleWindow?.[0]);
}

function endOfWindowDuration(
  recurringSchedule: RecurringScheduleDto
): Duration {
  return parseTimeStringToDuration(recurringSchedule.scheduleWindow?.[1]);
}

function RecurringSchedulesLoading() {
  return (
    <Stack direction="column" spacing={1} sx={scheduleDayRowsSx}>
      {Array.from({ length: 7 }, (_, i) => (
        <Skeleton key={i} variant="rounded" height={56} />
      ))}
    </Stack>
  );
}

export function RecurringSchedules() {
  const { t } = useTranslation();
  const { palette } = useTheme();
  const { errorToast, successToast } = useToast();

  const {
    batchesByDay,
    isLoading: isRecurringSchedulesLoading,
    error: recurringSchedulesError,
    refetch: refetchRecurringSchedules
  } = useGetRecurringSchedulesQuery(undefined, {
    selectFromResult: ({ data, ...rest }) => ({
      ...rest,
      batchesByDay: groupBy(data || [], (recurringWave) =>
        dayOfWeekAsString(recurringWave.dayOfWeek)
      )
    })
  });
  const {
    data: stagingAreas,
    isLoading: isStagingAreasLoading,
    error: stagingAreaError
  } = useGetStagingAreasQuery();
  const [deleteRecurringSchedule] = useDeleteRecurringScheduleMutation();

  const [recurringScheduleToEdit, setRecurringScheduleToEdit] =
    useState<RecurringScheduleDto | null>(null);
  const [recurringScheduleDayToCreate, setRecurringScheduleDayToCreate] =
    useState<DayOfWeekValue | null>(null);
  const [recurringScheduleToDelete, setRecurringScheduleToDelete] =
    useState<RecurringScheduleDto | null>(null);

  if (stagingAreaError || recurringSchedulesError) {
    return (
      <ErrorPanel
        message={getMessageFromRtkError(
          stagingAreaError || recurringSchedulesError
        )}
      />
    );
  }

  if (isRecurringSchedulesLoading || isStagingAreasLoading || !stagingAreas) {
    return <RecurringSchedulesLoading />;
  }

  function lookupStagingArea(recurringSchedule: RecurringScheduleDto) {
    const stagingArea = stagingAreas?.find(
      (sa) => sa.stagingAreaId === recurringSchedule.stagingAreaId
    );
    return stagingArea ? stagingArea.stagingAreaName : "N/A";
  }

  const handleDeleteSchedule = async () => {
    if (!recurringScheduleToDelete) {
      return;
    }
    try {
      await deleteRecurringSchedule(
        recurringScheduleToDelete.recurringScheduleId
      ).unwrap();
      await refetchRecurringSchedules();
      successToast(t("batch setting deleted"));
      setRecurringScheduleToDelete(null);
    } catch (error) {
      errorToast(getMessageFromRtkError(error));
    }
  };

  return (
    <>
      <Stack spacing={0} sx={scheduleDayRowsSx}>
        {daysOfTheWeek.map((dayOfWeek) => (
          <Accordion
            key={dayOfWeek}
            disableGutters
            elevation={0}
            sx={scheduleDayAccordionSx}
          >
            <AccordionSummary
              expandIcon={
                <ChevronRight20Px
                  aria-hidden
                  style={{ transform: "rotate(0deg)" }}
                />
              }
              sx={{
                minHeight: 56,
                px: 2,
                "& .MuiAccordionSummary-expandIconWrapper": {
                  transition: "transform 0.2s"
                },
                "& .MuiAccordionSummary-expandIconWrapper.Mui-expanded": {
                  transform: "rotate(90deg)"
                },
                "& .MuiAccordionSummary-content": {
                  my: 1.5
                }
              }}
            >
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                {t(dayOfWeek)}
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.5, pb: 2, pt: 0 }}>
              <Box sx={{ width: "100%", minWidth: 0 }}>
                {!batchesByDay[dayOfWeek]?.length ? (
                  <Typography variant="body1" textAlign="center" sx={{ py: 2 }}>
                    {t("there are no schedules for this day")}
                  </Typography>
                ) : (
                  <Timeline
                    position="right"
                    sx={{
                      [`& .${timelineOppositeContentClasses.root}`]: {
                        flex: "0 0 auto",
                        minWidth: "4.75rem",
                        pl: 0,
                        pr: 1,
                        py: 0.5
                      },
                      "& .MuiTimelineContent-root": {
                        flex: 1,
                        minWidth: 0,
                        pl: 1,
                        pr: 0,
                        py: 0.5
                      },
                      "& .MuiTimelineSeparator-root": {
                        mx: 0.5
                      },
                      m: 0,
                      p: 0
                    }}
                  >
                    {batchesByDay[dayOfWeek]
                      .sort(
                        (a, b) =>
                          startOfWindowDuration(a).asMilliseconds() -
                          startOfWindowDuration(b).asMilliseconds()
                      )
                      .map((recurringSchedule, i) => (
                        <TimelineItem
                          key={recurringSchedule.recurringScheduleId}
                        >
                          <TimelineOppositeContent>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              component="span"
                            >
                              {dayjs
                                .utc(
                                  startOfWindowDuration(
                                    recurringSchedule
                                  ).asMilliseconds()
                                )
                                .format("hh:mm A")}
                            </Typography>
                          </TimelineOppositeContent>
                          <TimelineSeparator>
                            <TimelineDot color="primary" />
                            <TimelineConnector />
                          </TimelineSeparator>
                          <TimelineContent>
                            <Stack
                              direction="row"
                              alignItems="center"
                              spacing={0.5}
                              sx={{ width: "100%", minWidth: 0 }}
                            >
                              <Paper elevation={0} sx={scheduleEntryCardSx}>
                                <Stack
                                  direction="row"
                                  alignItems="center"
                                  justifyContent="space-between"
                                >
                                  <Box sx={{ minWidth: 0, flex: 1 }}>
                                    <ScheduleOrderCount
                                      recurringSchedule={recurringSchedule}
                                    />
                                    {recurringSchedule.stagingAreaId ? (
                                      <Box sx={{ px: 2, pb: 1.5, pt: 0 }}>
                                        <Typography variant="body2">
                                          {t("assigned to staging area")}{" "}
                                          <Box
                                            component="span"
                                            sx={{ fontWeight: 700 }}
                                          >
                                            {lookupStagingArea(
                                              recurringSchedule
                                            )}
                                          </Box>
                                        </Typography>
                                      </Box>
                                    ) : null}
                                  </Box>
                                  <IconButton
                                    aria-label={`edit-${dayOfWeek}-${i}`}
                                    size="small"
                                    onClick={() => {
                                      setRecurringScheduleToEdit(
                                        recurringSchedule
                                      );
                                    }}
                                    sx={{
                                      mr: 0.5,
                                      flexShrink: 0,
                                      alignSelf: "center"
                                    }}
                                  >
                                    <Edit20Px
                                      style={{ fill: palette.primary.main }}
                                    />
                                  </IconButton>
                                </Stack>
                              </Paper>

                              <IconButton
                                aria-label={`delete-${dayOfWeek}-${i}`}
                                size="small"
                                onClick={() => {
                                  setRecurringScheduleToDelete(
                                    recurringSchedule
                                  );
                                }}
                                sx={{
                                  color: "text.secondary",
                                  flexShrink: 0,
                                  mr: -0.5
                                }}
                              >
                                <Remove20Px />
                              </IconButton>
                            </Stack>
                          </TimelineContent>
                        </TimelineItem>
                      ))}
                    <TimelineItem>
                      <TimelineOppositeContent>
                        <Typography variant="body2" color="text.secondary">
                          {dayjs
                            .utc(
                              endOfWindowDuration(
                                batchesByDay[dayOfWeek][
                                  batchesByDay[dayOfWeek].length - 1
                                ]
                              ).asMilliseconds()
                            )
                            .format("hh:mm A")}
                        </Typography>
                      </TimelineOppositeContent>
                      <TimelineSeparator>
                        <TimelineDot />
                      </TimelineSeparator>
                      <TimelineContent />
                    </TimelineItem>
                  </Timeline>
                )}
              </Box>

              <Button
                variant="text"
                startIcon={
                  <PlusCircle20Px style={{ fill: palette.text.secondary }} />
                }
                onClick={() =>
                  setRecurringScheduleDayToCreate(DayOfWeekMap[dayOfWeek])
                }
                sx={{
                  color: "text.secondary",
                  alignSelf: "flex-end",
                  display: "flex",
                  ml: "auto",
                  mt: 1,
                  "&:hover": {
                    color: "text.secondary",
                    backgroundColor: "action.hover"
                  }
                }}
              >
                {t("add schedule")}
              </Button>
            </AccordionDetails>
          </Accordion>
        ))}
      </Stack>

      {recurringScheduleToEdit && (
        <EditRecurringScheduleModal
          recurringSchedule={recurringScheduleToEdit}
          onClose={() => setRecurringScheduleToEdit(null)}
        />
      )}
      {recurringScheduleDayToCreate !== null && (
        <CreateRecurringScheduleModal
          dayOfWeekToCreate={recurringScheduleDayToCreate}
          onClose={() => setRecurringScheduleDayToCreate(null)}
        />
      )}
      {recurringScheduleToDelete && (
        <ConfirmationModal
          isOpen
          confirmCb={handleDeleteSchedule}
          closeCb={() => setRecurringScheduleToDelete(null)}
          modalTitle={t("delete schedule")}
          modalText={t("are you sure you want to delete this schedule?")}
          labelledById="delete-schedule-modal-title"
        />
      )}
    </>
  );
}
